import { Button, cn, Form, Spinner } from "@heroui/react";
import { IconX } from "@tabler/icons-react";
import type React from "react";
import { type FC, useEffect, useRef, useState } from "react";
import type { Area } from "react-easy-crop";
import Webcam from "react-webcam";
import { Content } from "../components/content";
import { MainLayout } from "../components/mainlayout";
import { NavigationButtons } from "../components/navigation-button";
import { useResumeBuilder } from "../context";
import { useNavigateWithParams } from "../hooks";
import { getCroppedImg } from "./cropImage";
import { PhotoCropper } from "./photo-cropper";

const mirrorBase64Image = async (base64: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                return reject(new Error("Could not get canvas context"));
            }

            canvas.width = image.width;
            canvas.height = image.height;

            // Flip horizontally
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);

            // Draw the mirrored image
            ctx.drawImage(image, 0, 0);

            // Return the new base64 image
            resolve(canvas.toDataURL());
        };
        image.onerror = (err) => reject(err);
        image.src = base64;
    });
};

const COLORS = [
    { name: "Slate", value: "#64748b" },
    { name: "Amber", value: "#f59e0b" },
    { name: "Rose", value: "#f43f5e" },
    { name: "Teal", value: "#14b8a6" },
    { name: "Indigo", value: "#6366f1" },
    { name: "Sky", value: "#0ea5e9" },
    { name: "Emerald", value: "#10b981" },
    { name: "White", value: "#ffffff" },
    { name: "Black", value: "#000000" },
];

const ResumeBuilder3Page = () => {
    const navigate = useNavigateWithParams();
    const { resumeContent, updateStep3 } = useResumeBuilder();
    const [color, setColor] = useState(resumeContent.step3.color);
    const [showCamera, setShowCamera] = useState(false);
    const [photo, setPhoto] = useState<string | null>(null);
    const [showCropper, setShowCropper] = useState(false);
    const [isLoadingImage, setIsLoadingImage] = useState(false);
    const [isCameraPicture, setIsCameraPicture] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
        null,
    );
    const fileInputRef = useRef<HTMLInputElement>(null);
    const webcamRef = useRef<Webcam>(null);

    useEffect(() => {
        setColor(resumeContent.step3.color);
        setPhoto(resumeContent.step3.photo);
    }, [resumeContent.step3]);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsLoadingImage(true);
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhoto(reader.result as string);
                setShowCropper(true);
                setIsLoadingImage(false);
                setIsCameraPicture(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCameraCapture = () => {
        if (webcamRef.current) {
            const photoData = webcamRef.current.getScreenshot();
            setPhoto(photoData);
            setShowCamera(false);
            setShowCropper(true);
            setIsCameraPicture(true);
        }
    };

    const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleCropSave = async () => {
        if (photo && croppedAreaPixels) {
            try {
                const croppedImage = await getCroppedImg(
                    photo,
                    croppedAreaPixels,
                );
                setPhoto(croppedImage);
                setShowCropper(false);
            } catch (error) {
                console.error("Error cropping image:", error);
            }
        }
    };

    const handleRemovePhoto = () => {
        setPhoto(null);
        fileInputRef.current.value = "";
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveAndProceed();
    };

    const saveAndProceed = () => {
        if (!photo) {
            setErrorMessage(
                "Ein Foto ist erforderlich.",
            );
            return;
        }
        setErrorMessage(null);
        
        updateStep3(photo, color);
        navigate("/preview");
    };

    const navigateToPreview = () => {
        updateStep3(photo, color);
    };

    return (
        <MainLayout>
            <Content
                title="Foto & Farbe"
                description="Es muss kein klassisches Businessfoto sein. Wir freuen uns über
            jedes Bild."
                currentStep={3}
            >
                <Form
                    onSubmit={handleSubmit}
                    className="gap-5"
                >
                    <PhotoSection
                        handleFileUpload={handleFileUpload}
                        showCamera={showCamera}
                        setShowCamera={setShowCamera}
                        handleCameraCapture={handleCameraCapture}
                        webcamRef={webcamRef}
                        fileInputRef={fileInputRef}
                        photo={photo}
                        onRemovePhoto={handleRemovePhoto}
                        isLoading={isLoadingImage}
                    />

                    {errorMessage && (
                        <div className="mt-4 flex w-full items-center justify-center">
                            <p className="text-danger text-sm">
                                {errorMessage}
                            </p>
                        </div>
                    )}

                    <div className="h-0 w-full" />

                    <ColorPickerSection
                        selectedColor={color}
                        onColorSelect={setColor}
                    />

                    <NavigationButtons
                        onBack={() => navigate(-1)}
                        onNext={saveAndProceed}
                        onPreview={navigateToPreview}
                        nextButtonText="Finale Vorschau →"
                    />
                </Form>

                {showCropper && photo && (
                    <PhotoCropper
                        photo={photo}
                        onCropComplete={onCropComplete}
                        onClose={() => setShowCropper(false)}
                        onSave={handleCropSave}
                        onRetake={() => {
                            setPhoto(null);
                            setShowCropper(false);
                            setShowCamera(true);
                        }}
                        onMirror={async () => {
                            const mirroredImage =
                                await mirrorBase64Image(photo);
                            setPhoto(mirroredImage);
                        }}
                        isCameraPicture={isCameraPicture}
                    />
                )}
            </Content>
        </MainLayout>
    );
};

interface PhotoSectionProps {
    handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    showCamera: boolean;
    setShowCamera: (show: boolean) => void;
    handleCameraCapture: () => void;
    webcamRef: React.RefObject<Webcam>;
    fileInputRef: React.RefObject<HTMLInputElement>;
    photo: string | null;
    onRemovePhoto: () => void;
    isLoading: boolean;
}

const PhotoSection: FC<PhotoSectionProps> = ({
    handleFileUpload,
    showCamera,
    setShowCamera,
    handleCameraCapture,
    webcamRef,
    fileInputRef,
    photo,
    onRemovePhoto,
    isLoading,
}) => (
    <section className="flex w-full flex-col items-center gap-4">
        {isLoading && (
            <div className="flex justify-center">
                <p>Bild wird geladen...</p>
                <Spinner className="ml-2" />
            </div>
        )}

        {showCamera && (
            <CameraOverlay
                webcamRef={webcamRef}
                handleCameraCapture={handleCameraCapture}
                setShowCamera={setShowCamera}
            />
        )}

        {photo && <PhotoPreview photo={photo} onRemove={onRemovePhoto} />}

        <div className="flex w-full xs:flex-row flex-col justify-center gap-4">
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
            />
            <Button
                onPress={() => fileInputRef.current?.click()}
                variant="bordered"
                fullWidth={true}
            >
                {photo ? "Anderes Foto hochladen" : "Foto hochladen"}
            </Button>
            <Button
                className="hidden md:block"
                onPress={() => setShowCamera(true)}
                variant="bordered"
                fullWidth={true}
            >
                Foto aufnehmen
            </Button>
        </div>
    </section>
);

const CameraOverlay = ({ webcamRef, handleCameraCapture, setShowCamera }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="rounded-lg bg-white p-4">
            <Webcam
                ref={webcamRef}
                videoConstraints={{ facingMode: "user" }}
                mirrored={true}
                screenshotFormat="image/jpeg"
                className="mb-4"
            />
            <div className="flex justify-between">
                <Button
                    color="danger"
                    variant="light"
                    onPress={() => setShowCamera(false)}
                >
                    Abbrechen
                </Button>
                <Button color="primary" onClick={handleCameraCapture}>
                    Foto aufnehmen
                </Button>
            </div>
        </div>
    </div>
);

const PhotoPreview = ({ photo, onRemove }) => (
    <div className="flex justify-center">
        <div className="group relative">
            <img src={photo} alt="Preview" className="max-w-[200px] rounded-2xl shadow" />
            <Button
                onPress={onRemove}
                className="-translate-y-1/2 absolute top-0 right-0 translate-x-1/2 rounded-full p-1"
                type="button"
                aria-label="Remove photo"
                isIconOnly={true}
                size="sm"
                variant="shadow"
            >
                <IconX size={16} />
            </Button>
        </div>
    </div>
);

const ColorPickerSection = ({ selectedColor, onColorSelect }) => (
    <section className="flex flex-col items-center">
        <p className="mb-4">Wählen Sie eine Farbe für Ihren Lebenslauf:</p>
        <div className="flex flex-wrap justify-center gap-4">
            {COLORS.map((color) => (
                <button
                    key={color.value}
                    onClick={() => onColorSelect(color.value)}
                    className={cn(
                        "h-12 w-12 rounded-full border border-default-200 transition-transform",
                        selectedColor === color.value
                            ? "scale-110 ring-4 ring-offset-2"
                            : "hover:scale-105",
                    )}
                    style={{
                        backgroundColor: color.value,
                    }}
                    type="button"
                    aria-label={`Select ${color.name} color`}
                />
            ))}
        </div>
    </section>
);

export { ResumeBuilder3Page };
